import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { usePermission } from '../../../shared/hooks/usePermission';
import { RoleService } from '../../../shared/services/roleService';

const ALL_ACTIONS = ['read', 'create', 'update', 'delete', 'export'];

function PermissionMatrix({ permissions }) {
  if (!permissions || Object.keys(permissions).length === 0) {
    return <p className="text-xs text-gray-400 py-2">No permissions assigned</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="text-xs w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-3 py-2 font-medium text-gray-500 w-40">Module</th>
            {ALL_ACTIONS.map((a) => (
              <th key={a} className="text-center px-2 py-2 font-medium text-gray-500 capitalize w-16">{a}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {Object.entries(permissions).map(([moduleSlug, actions]) => (
            <tr key={moduleSlug} className="hover:bg-gray-50/50">
              <td className="px-3 py-2 font-medium text-gray-700 capitalize">
                {moduleSlug.replace(/-/g, ' ')}
              </td>
              {ALL_ACTIONS.map((action) => {
                const has = Array.isArray(actions) && actions.includes(action);
                return (
                  <td key={action} className="text-center px-2 py-2">
                    {has ? (
                      <CheckIcon className="w-3.5 h-3.5 text-blue-600 mx-auto" />
                    ) : (
                      <span className="text-gray-200 text-base leading-none">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleRow({ role, expandedId, onToggle, moduleFilter }) {
  const isExpanded = expandedId === role.id;
  const permissions = role.permissions || {};

  // Filter permissions by module if filter is active
  const filteredPermissions = moduleFilter
    ? Object.fromEntries(
        Object.entries(permissions).filter(([slug]) => slug === moduleFilter)
      )
    : permissions;

  const moduleKeys = Object.keys(filteredPermissions);
  // If module filter is active and this role has no matching module, hide it
  if (moduleFilter && moduleKeys.length === 0) return null;

  return (
    <>
      <tr
        className="hover:bg-gray-50/50 cursor-pointer"
        onClick={() => onToggle(role.id)}
      >
        <td className="px-5 py-3">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="font-medium text-gray-800">{role.name}</span>
          </div>
        </td>
        <td className="px-5 py-3 text-sm text-gray-500">{role.description || '—'}</td>
        <td className="px-5 py-3">
          {role.isSuperAdmin ? (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
              Super Admin
            </span>
          ) : (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
              Standard
            </span>
          )}
        </td>
        <td className="px-5 py-3">
          {role.isSuperAdmin ? (
            <span className="text-xs text-purple-500">All modules</span>
          ) : moduleKeys.length === 0 ? (
            <span className="text-xs text-gray-400">No permissions</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {moduleKeys.slice(0, 5).map((m) => (
                <span key={m} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {m.replace(/-/g, ' ')} ({(filteredPermissions[m] || []).length})
                </span>
              ))}
              {moduleKeys.length > 5 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                  +{moduleKeys.length - 5} more
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
      {isExpanded && !role.isSuperAdmin && (
        <tr>
          <td colSpan={4} className="px-5 pb-4 pt-0 bg-gray-50/30">
            <div className="ml-6 border border-gray-100 rounded-lg overflow-hidden">
              <PermissionMatrix permissions={filteredPermissions} />
            </div>
          </td>
        </tr>
      )}
      {isExpanded && role.isSuperAdmin && (
        <tr>
          <td colSpan={4} className="px-5 pb-4 pt-0 bg-gray-50/30">
            <div className="ml-6 p-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-600">
              Super Admin has full access to all modules and actions.
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PermissionsTab() {
  const canRead = usePermission('roles', 'read');

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await RoleService.getAll({ limit: 100 });
      setRoles(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    fetchRoles();
  }, [canRead, fetchRoles]);

  const handleToggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (!canRead) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">
        You do not have permission to view permissions.
      </div>
    );
  }

  // Collect all unique module slugs from all roles for filter dropdown
  const allModuleSlugs = Array.from(
    new Set(roles.flatMap((r) => Object.keys(r.permissions || {})))
  ).sort();

  const filteredRoles = roles.filter((r) => {
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (moduleFilter) {
      if (r.isSuperAdmin) return true; // super admin matches everything
      if (!r.permissions || !r.permissions[moduleFilter]) return false;
    }
    return true;
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by role name..."
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
        />
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Modules</option>
          {allModuleSlugs.map((slug) => (
            <option key={slug} value={slug}>
              {slug.replace(/-/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 font-medium text-gray-600 w-48">Role Name</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Description</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600 w-32">Type</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Permissions Summary</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 4 }).map((__, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-4 bg-gray-100 rounded w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredRoles.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-gray-400">
                    No roles found
                  </td>
                </tr>
              ) : (
                filteredRoles.map((role) => (
                  <RoleRow
                    key={role.id}
                    role={role}
                    expandedId={expandedId}
                    onToggle={handleToggle}
                    moduleFilter={moduleFilter}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
